import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { InlineStack, Link, Stack, Tag, Truncated } from '@customerio/pluma-components/react';
import plur from 'plur';
import type { EnvironmentsEventAttributeSummary } from '@customerio/hydra-ui-api';
import * as styles from './event-attribute-tags.css';

/**
 * An event's attributes, as many as three rows of them, and a way to the
 * rest.
 *
 * The compact form of the item page's table: it fits in a details panel,
 * and the names alone answer what an event carries. Both surfaces show it
 * — the item page beside its own tab, the drawer instead of one.
 */

/** How many rows of tags the panel gives them, as the page this replaces does. */
const MAX_ROWS = 3;

/**
 * How many tags are laid out on the first attempt. An event can carry
 * hundreds and each tag is a tooltip, so they are not all mounted to fill
 * three rows — but a wide panel and short names can fit more than this, so
 * a batch that turns out to fill every row is followed by a larger one
 * rather than trusted.
 */
const FIRST_BATCH = 36;

type EventAttributeTagsProps = {
  /** Newest first, as the read already sorts them. */
  attributes: readonly EnvironmentsEventAttributeSummary[];
  /** The item page's Event attributes tab. */
  allHref: string;
  /**
   * Whether following the link leaves the surface. The page this replaces
   * points onward with a chevron where the tab is on the same page and
   * opens a new one from the drawer
   * (`item-details/event-attributes/index.gts:154-158`).
   */
  isLeaving: boolean;
  /**
   * Switches to the tab in place, for a surface already on the page that
   * holds it. Without it the href is all there is, and the router reads
   * `?tab=attributes` as part of the event's name — right by accident, but
   * it preloads a route for an event nobody has.
   */
  onActivate?: () => void;
};

export function EventAttributeTags({ attributes, allHref, isLeaving, onActivate }: EventAttributeTagsProps) {
  const container = useRef<HTMLDivElement>(null);
  // How many are laid out to be counted, and how many of those fit. `shown`
  // is null while a batch is waiting to be measured: counting against an
  // already-clamped set could only ever shrink it, so everything that
  // invalidates the count comes back through here rather than re-counting
  // in place.
  const [batch, setBatch] = useState(FIRST_BATCH);
  const [shown, setShown] = useState<number | null>(null);
  const candidates = attributes.slice(0, batch);

  // A different set of names is a different layout, and a batch sized for
  // the last one says nothing about this one.
  useLayoutEffect(() => {
    setBatch(FIRST_BATCH);
    setShown(null);
  }, [attributes]);

  useLayoutEffect(() => {
    const node = container.current;
    // Nothing to count while the panel is off the layout — a drawer that
    // has been closed keeps its body mounted. Every child of a hidden
    // subtree reports `offsetTop` 0, which reads as one row holding
    // everything, and the growth below would take that as licence to mount
    // the whole set out of sight.
    if (node == null || shown != null || node.clientWidth === 0) {
      return;
    }

    const fitting = rowsThatFit(node);
    // Every tag offered fitted, so the rows may not be full — a wider panel
    // or shorter names hold more than the batch did. Offer more and count
    // again; the batch cannot outgrow the set, so this settles.
    if (fitting === candidates.length && candidates.length < attributes.length) {
      setBatch(Math.min(candidates.length * 2, attributes.length));
      return;
    }
    setShown(fitting);
  }, [shown, batch, candidates.length, attributes.length]);

  // Width decides how many fit, so a resized panel has to be counted again.
  // Height is what the clamp itself changes, and reacting to that would
  // never settle.
  useEffect(() => {
    const node = container.current;
    if (node == null) {
      return;
    }

    // Seeded from the observer's own first callback rather than a
    // `getBoundingClientRect`, which measures the border box where
    // `contentRect` measures the content box.
    let last: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      // A panel going off the layout reports zero; it is not a new width to
      // count against, and the count it had still holds when it returns.
      if (width == null || width === 0 || width === last) {
        return;
      }
      const isFirst = last == null;
      last = width;
      if (!isFirst) {
        setShown(null);
      }
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // A webfont arriving after the first layout rewraps the rows without
  // changing the panel's width, so the observer above never hears about it.
  useEffect(() => {
    if (typeof document === 'undefined' || document.fonts == null) {
      return;
    }

    let cancelled = false;
    void document.fonts.ready.then(() => {
      if (!cancelled) {
        setShown(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Stack gap="100">
      <InlineStack ref={container} wrap="wrap" gap="100" className={shown == null ? styles.measuring : undefined}>
        {candidates.slice(0, shown ?? candidates.length).map((attribute) => (
          <Tag key={attribute.name} className={styles.tag}>
            <Truncated tooltip={attribute.name}>{attribute.name}</Truncated>
          </Tag>
        ))}
      </InlineStack>

      <Link
        href={allHref}
        variant="secondary"
        icon={isLeaving ? 'exit' : 'chevron-right'}
        iconPosition="trailing"
        target={isLeaving ? '_blank' : undefined}
        // The href stays, so middle-click and "open in new tab" still work
        // (`pages/settings/api-keys/workspace-page.tsx:61-66` does the same).
        onClick={
          onActivate == null
            ? undefined
            : (event: React.MouseEvent) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                  return;
                }
                event.preventDefault();
                onActivate();
              }
        }
      >
        View all {attributes.length.toLocaleString()} event {plur('attribute', attributes.length)}
      </Link>
    </Stack>
  );
}

/** How many of the laid-out tags sit within the first three rows. */
function rowsThatFit(node: HTMLDivElement): number {
  const tags = [...node.querySelectorAll<HTMLElement>(':scope > *')];
  const rows: number[] = [];

  for (const [index, tag] of tags.entries()) {
    const top = tag.offsetTop;
    if (!rows.includes(top)) {
      rows.push(top);
    }
    if (rows.length > MAX_ROWS) {
      return index;
    }
  }

  return tags.length;
}
